// =============================================================================
// KIT VISUAL DO CHECKOUT PÚBLICO — a identidade "Linoge" num lugar só.
//
// Fonte da verdade: `CheckoutFlow.tsx` (o checkout de compra, portado 1:1 do
// Reservei por pedido do dono). Até 2026-08-24 cada tela satélite (cobrança,
// troca de plano, pagamento, sucesso) carregava uma CÓPIA inline deste visual
// — e as que esqueciam de copiar (pay/success) caíam nos tokens escuros do
// painel admin. Este arquivo mata a cópia: visual muda AQUI, todo mundo segue.
//
// A identidade:
//   - fundo creme #F5F1EC, texto preto, [color-scheme:light] (o app é dark-first)
//   - fonte Open Sans (carregada por rota, só nas páginas white-label)
//   - título editorial gigante condensado com brushstroke (/checkout-bg.png)
//   - seções: título uppercase tracking-[0.2em] sobre régua preta de 1px
//   - acento teal (teal-500/600/700), CTA preto, cards brancos
//
// PROIBIDO aqui: tokens do painel admin (bg-page, bg-panel, text-ink,
// text-muted, border-line, text-good, text-info…). Há teste travando isso
// (cobranca.spec.tsx e pay.spec.tsx varrem o HTML renderizado).
// =============================================================================

import { motion } from 'framer-motion';
import { AlertCircle, Copy, Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { cn } from '@control-center/ui';

/** Easing assinatura do checkout (expo-out — mesma curva do CheckoutFlow). */
export const CHECKOUT_EXPO_OUT = [0.16, 1, 0.3, 1] as const;

/** Creme do fundo — o mesmo do CheckoutFlow/Reservei. */
export const CHECKOUT_CREAM = '#F5F1EC';

// -----------------------------------------------------------------------------
// CheckoutShell — moldura da página: creme, header de marca, título editorial.
// -----------------------------------------------------------------------------

export interface CheckoutShellProps {
  /** Título editorial gigante (caixa alta), ex.: "PAGAMENTO". */
  title: string;
  /** Nome exibido ao lado do logo. Default: Opus Owl (white-label usa o SaaS). */
  brandName?: string;
  /** Quando presente, logo+nome viram link pra home do SaaS. */
  homeUrl?: string | null;
  children: React.ReactNode;
}

export function CheckoutShell({
  title,
  brandName = 'Opus Owl',
  homeUrl = null,
  children,
}: CheckoutShellProps): JSX.Element {
  // Open Sans só nesta rota (mesmo id do CheckoutFlow — não duplica o <link>).
  useEffect(() => {
    const id = 'cc-checkout-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;500;600;700;800&family=Playfair+Display:ital,wght@1,700;1,900&display=swap';
    document.head.appendChild(link);
  }, []);

  const brand = (
    <>
      <img src="/logo-opus-ico.png" alt="" className="h-8 w-8 rounded-full" />
      <span className="text-[15px] font-bold tracking-tight text-black">{brandName}</span>
    </>
  );

  return (
    <div
      className="min-h-dvh bg-[#F5F1EC] antialiased [color-scheme:light]"
      style={{ fontFamily: "'Open Sans', system-ui, sans-serif" }}
    >
      <div className="mx-auto w-full max-w-[1200px] px-5 pb-24 pt-6 lg:px-8 lg:pt-10">
        <header className="mb-8 flex items-center justify-between gap-3 lg:mb-10">
          {homeUrl ? (
            <a
              href={homeUrl}
              aria-label="Ir para a página inicial"
              className="flex items-center gap-2 transition-opacity hover:opacity-70"
            >
              {brand}
            </a>
          ) : (
            <div className="flex items-center gap-2">{brand}</div>
          )}
          <div className="flex items-center gap-2 text-[13px] font-medium uppercase tracking-[0.15em] text-black/40">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-teal-500" />
            Pagamento seguro
          </div>
        </header>

        <EditorialTitle title={title} />

        <main>{children}</main>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// EditorialTitle — o título gigante com brushstroke desenhando (draw effect).
// -----------------------------------------------------------------------------

export function EditorialTitle({ title }: { title: string }): JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: CHECKOUT_EXPO_OUT }}
      className="relative mb-10 flex h-[120px] items-center lg:mb-14 lg:h-[180px]"
    >
      <motion.img
        initial={{ clipPath: 'inset(0 100% 0 0)', opacity: 0 }}
        animate={{ clipPath: 'inset(0 0% 0 0)', opacity: 0.75 }}
        transition={{
          clipPath: { duration: 2.8, ease: CHECKOUT_EXPO_OUT, delay: 0.3 },
          opacity: { duration: 0.6, ease: CHECKOUT_EXPO_OUT, delay: 0.3 },
        }}
        src="/checkout-bg.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full select-none object-cover object-center mix-blend-multiply"
        style={{
          maskImage:
            'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
        }}
      />
      {/* Halo creme atrás do texto pra garantir contraste sobre o brushstroke */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-1/2 h-[110%] w-[55%] -translate-y-1/2"
        style={{
          background:
            'radial-gradient(ellipse at left center, rgba(245,241,236,0.85) 0%, rgba(245,241,236,0.6) 40%, rgba(245,241,236,0) 75%)',
        }}
      />
      <motion.h1
        initial={{ opacity: 0, letterSpacing: '0em' }}
        animate={{ opacity: 1, letterSpacing: '-0.04em' }}
        transition={{ duration: 1, ease: CHECKOUT_EXPO_OUT, delay: 0.2 }}
        className="relative z-10 text-[52px] font-black leading-[0.85] text-black mix-blend-multiply lg:text-[86px]"
        style={{ fontStretch: 'condensed' }}
      >
        {title}
      </motion.h1>
    </motion.div>
  );
}

// -----------------------------------------------------------------------------
// CheckoutSection — título curto em caixa alta sobre régua preta, sem card.
// (O checkout não empilha caixas na coluna principal; card só no resumo.)
// -----------------------------------------------------------------------------

/** Só o título + régua — pra layouts que gerenciam a própria seção. */
export function CheckoutSectionTitle({
  icon: Icon,
  children,
}: {
  icon?: React.ComponentType<{ size?: number | string; strokeWidth?: number | string }>;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <>
      <h2 className="mb-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-black">
        {Icon && <Icon size={13} strokeWidth={2.2} aria-hidden="true" />}
        {children}
      </h2>
      <div className="mb-6 h-px bg-black" />
    </>
  );
}

export function CheckoutSection({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ComponentType<{ size?: number | string; strokeWidth?: number | string }>;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="mb-12">
      <CheckoutSectionTitle icon={icon}>{title}</CheckoutSectionTitle>
      {children}
    </section>
  );
}

// -----------------------------------------------------------------------------
// CheckoutCenterCard — card branco centralizado pra estados terminais
// (sucesso, já pago, erro de link, agendado…).
// -----------------------------------------------------------------------------

export function CheckoutCenterCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn(
        'mx-auto max-w-lg rounded-[16px] border border-black/[0.08] bg-white p-8 text-center shadow-[0_1px_2px_rgba(0,0,0,0.05)]',
        className,
      )}
    >
      {children}
    </div>
  );
}

// -----------------------------------------------------------------------------
// CopyCodeField — código (Pix copia-e-cola, linha digitável) + botão Copiar.
// -----------------------------------------------------------------------------

export function CopyCodeField({
  code,
  label,
  successMessage = 'Código copiado!',
}: {
  code: string;
  /** Rótulo pequeno acima do campo, ex.: "Ou copie o código". Opcional. */
  label?: string;
  successMessage?: string;
}): JSX.Element {
  function copy(): void {
    void navigator.clipboard.writeText(code).then(
      () => toast.success(successMessage),
      () => toast.error('Não conseguimos copiar. Selecione e copie manualmente.'),
    );
  }

  return (
    <div>
      {label && (
        <div className="text-[11px] font-medium uppercase tracking-wider text-black/30">
          {label}
        </div>
      )}
      <div
        className={cn(
          'flex items-center gap-2 rounded-[10px] border border-black/10 bg-black/[0.035] p-2',
          label && 'mt-2',
        )}
      >
        {/*
          `min-w-0` NÃO é decoração — sem ele o `truncate` não funciona.
          Flex item tem `min-width: auto` por padrão, então um código Pix de
          ~180 caracteres SEM UM ESPAÇO faz o <code> exigir a largura inteira
          do conteúdo; o `overflow: hidden` do truncate não tem o que cortar e
          o item empurra tudo. Bug real 2026-08-31: a tela de pagamento
          estourava na horizontal e jogava o card "Resumo" pra fora da viewport.
        */}
        <code className="min-w-0 flex-1 truncate font-mono text-[11px] text-black/55">{code}</code>
        <button
          type="button"
          onClick={copy}
          className="inline-flex shrink-0 items-center gap-1 rounded-[8px] bg-black px-3 py-1.5 text-[12px] font-semibold text-white hover:opacity-90"
        >
          <Copy size={12} strokeWidth={2.5} />
          Copiar
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// CheckoutWaitingFooter — "aguardando pagamento…" com spinner.
// -----------------------------------------------------------------------------

export function CheckoutWaitingFooter({
  children = 'Aguardando confirmação do pagamento…',
}: {
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="mt-6 flex items-center justify-center gap-2 text-[12px] text-black/45">
      <Loader2 size={12} className="animate-spin" />
      {children}
    </div>
  );
}

// -----------------------------------------------------------------------------
// CheckoutErrorBox — erro amigável com fallback opcional pro Asaas.
// -----------------------------------------------------------------------------

export function CheckoutErrorBox({
  message,
  fallbackUrl,
}: {
  message: string;
  fallbackUrl?: string | null;
}): JSX.Element {
  return (
    <div className="mx-auto max-w-[420px] rounded-[14px] border border-red-500/40 bg-red-500/5 p-6 text-center">
      <AlertCircle size={28} strokeWidth={1.8} className="mx-auto text-red-600" />
      <p className="mt-3 text-[13px] text-black/70">{message}</p>
      {fallbackUrl && (
        <a
          href={fallbackUrl}
          className="mt-4 inline-flex items-center gap-2 rounded-[10px] bg-black px-4 py-2 text-[12px] font-semibold text-white"
        >
          Abrir checkout do Asaas
        </a>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// CardInput — campo do formulário de cartão (cobrança e troca de plano).
// -----------------------------------------------------------------------------

export function CardInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] font-medium uppercase tracking-wider text-black/30">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-10 rounded-[10px] border border-black/10 bg-black/[0.035] px-3 text-[13px] text-black outline-none transition-colors focus:border-black/25"
      />
    </label>
  );
}
